import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import {
  Sparkle,
  Globe,
  Newspaper,
  Brain,
  TrendUp,
  ShieldCheck,
  Clock,
  Users,
  ArrowRight,
  Lightning,
  ChartBar,
  Heartbeat,
  Buildings,
  CurrencyDollar,
} from "@phosphor-icons/react";

// ─── Structured data for SEO ────────────────────────────────────────────────
const ABOUT_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  name: "About TrendMing",
  description:
    "TrendMing is a modern AI-powered news and content platform delivering real-time global trends, technology insights, business intelligence, and world news.",
  url: "https://trendming.com/about",
  isPartOf: {
    "@type": "WebSite",
    name: "TrendMing",
    url: "https://trendming.com",
  },
  publisher: {
    "@type": "Organization",
    name: "TrendMing",
    url: "https://trendming.com",
    logo: {
      "@type": "ImageObject",
      url: "https://trendming.com/logo.png",
    },
    sameAs: [],
  },
};

// ─── Data constants ──────────────────────────────────────────────────────────
const COVERAGE_AREAS = [
  { icon: Brain, label: "AI & Technology", desc: "Deep dives into artificial intelligence, machine learning, and the tools reshaping industries." },
  { icon: Buildings, label: "Business & Markets", desc: "Corporate strategy, startups, mergers, and the forces driving global commerce." },
  { icon: Globe, label: "World News", desc: "Breaking international events, geopolitics, and regional developments that matter." },
  { icon: CurrencyDollar, label: "Finance & Economy", desc: "Markets, crypto, macroeconomics, and personal finance intelligence in real time." },
  { icon: Heartbeat, label: "Health & Science", desc: "Medical breakthroughs, wellness research, and scientific discoveries made accessible." },
  { icon: TrendUp, label: "Trending Topics", desc: "Viral stories, cultural moments, and the conversations dominating the global feed." },
];

const WHY_CHOOSE = [
  {
    icon: Clock,
    title: "Real-Time Intelligence",
    body: "Our AI pipeline continuously monitors thousands of sources worldwide, surfacing what matters within minutes — not hours.",
  },
  {
    icon: Brain,
    title: "AI-Curated Relevance",
    body: "Machine learning models rank, filter, and personalise your feed so every story you see earns its place.",
  },
  {
    icon: Globe,
    title: "Global-First Perspective",
    body: "We aggregate signals from 50+ countries, giving you a view of the world that no single local outlet can match.",
  },
  {
    icon: ShieldCheck,
    title: "Source Credibility Layer",
    body: "Every story is tagged with its origin. We surface provenance so you can read with context and confidence.",
  },
  {
    icon: Sparkle,
    title: "Creator-Grade Content Kits",
    body: "Trending headlines become ready-to-publish threads, scripts, and summaries — fuelling your content calendar effortlessly.",
  },
  {
    icon: ChartBar,
    title: "Trend Velocity Signals",
    body: "See not just what is trending but how fast — giving you a decisive early-mover edge on any topic.",
  },
];

const AI_FEATURES = [
  { label: "Smart Summaries", desc: "Multi-paragraph AI summaries that respect nuance." },
  { label: "Topic Clusters", desc: "Stories automatically grouped by theme and narrative thread." },
  { label: "Content Kits", desc: "One-click social posts, threads, and scripts from any article." },
  { label: "Personalised Feed", desc: "Ranking adapts to your reading history and preferences." },
  { label: "Multilingual Sources", desc: "Global sources translated and surfaced in English." },
  { label: "Trend Forecasting", desc: "Early-signal detection before stories go mainstream." },
];

const STATS = [
  { value: "50+", label: "Countries covered" },
  { value: "10K+", label: "Sources monitored" },
  { value: "Real-time", label: "Content refresh" },
  { value: "6", label: "Core topic verticals" },
];

// ─── Sub-components ──────────────────────────────────────────────────────────
function SectionLabel({ children }) {
  return (
    <span className="text-xs font-semibold uppercase tracking-wider text-blue-600">
      {children}
    </span>
  );
}

function Divider() {
  return <div className="border-t border-slate-200 my-16 md:my-24" />;
}

// ─── Page ────────────────────────────────────────────────────────────────────
export default function AboutPage() {
  return (
    <>
      <SEO
        title="About TrendMing"
        description="How TrendMing uses AI to filter and summarize the news that matters, for free."
        canonical="https://trendming.com/about"
        ogType="website"
        structuredData={[ABOUT_SCHEMA]}
      />

      <main className="max-w-[1200px] mx-auto px-4 md:px-8 py-12 md:py-20">

        {/* ── Hero ─────────────────────────────────────────────────────────── */}
        <section aria-labelledby="about-heading">
          <SectionLabel>/ About us</SectionLabel>
          <h1
            id="about-heading"
            className="font-bold text-4xl md:text-6xl lg:text-7xl mt-4 mb-6 leading-[1.05] text-slate-900"
          >
            The pulse of the{" "}
            <span className="text-blue-600">world's trends</span>,<br className="hidden md:block" />
            {" "}powered by AI.
          </h1>
          <p className="text-slate-600 text-lg md:text-xl max-w-3xl leading-relaxed mb-10">
            TrendMing is where artificial intelligence meets global journalism. We aggregate, analyse,
            and surface the stories that are shaping technology, business, finance, health, and culture
            — so you stay ahead, not behind.
          </p>

          {/* Stat row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px border border-slate-200 bg-slate-200 mb-4">
            {STATS.map(({ value, label }) => (
              <div key={label} className="bg-white px-6 py-5">
                <p className="font-bold text-3xl md:text-4xl text-slate-900">
                  {value}
                </p>
                <p className="text-xs uppercase tracking-wider text-slate-400 mt-1">
                  {label}
                </p>
              </div>
            ))}
          </div>
        </section>

        <Divider />

        {/* ── Who we are ───────────────────────────────────────────────────── */}
        <section aria-labelledby="who-heading" className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20 items-start">
          <div>
            <SectionLabel>/ Who we are</SectionLabel>
            <h2
              id="who-heading"
              className="font-bold text-3xl md:text-4xl mt-3 mb-6 text-slate-900"
            >
              Built for the information age
            </h2>
          </div>
          <div className="space-y-4 text-slate-600 text-base leading-relaxed">
            <p>
              TrendMing was founded on a simple conviction: in a world overwhelmed by noise, the
              right information at the right moment is a competitive advantage. We built a platform
              that cuts through the volume and delivers signal — fast, clear, and contextualised.
            </p>
            <p>
              We combine large-scale news aggregation with AI summarisation, trend-velocity analytics,
              and a creator toolkit, giving individuals and professionals the edge they need whether
              they're researching, investing, creating content, or simply staying informed.
            </p>
            <p>
              Our team spans engineers, journalists, and data scientists united by one mission: make
              global intelligence accessible to everyone, not just those with Bloomberg terminals.
            </p>
          </div>
        </section>

        <Divider />

        {/* ── Mission & Vision ─────────────────────────────────────────────── */}
        <section aria-labelledby="mission-heading">
          <SectionLabel>/ Mission &amp; Vision</SectionLabel>
          <h2
            id="mission-heading"
            className="font-bold text-3xl md:text-4xl mt-3 mb-10 text-slate-900"
          >
            What drives us
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Mission */}
            <div className="border border-slate-200 bg-white p-8 md:p-10 rounded-xl">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-8 h-8 bg-blue-600 flex items-center justify-center shrink-0 rounded-lg">
                  <Lightning size={16} weight="fill" className="text-white" />
                </div>
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Our Mission
                </span>
              </div>
              <h3 className="font-bold text-2xl md:text-3xl mb-4 text-slate-900">
                Democratise global intelligence
              </h3>
              <p className="text-slate-600 text-base leading-relaxed">
                To make the world's most relevant, real-time information available to every person —
                regardless of geography, budget, or background — through AI that amplifies human
                understanding rather than replacing it.
              </p>
            </div>

            {/* Vision */}
            <div className="border border-slate-200 bg-white p-8 md:p-10 rounded-xl">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-8 h-8 border border-blue-600 flex items-center justify-center shrink-0 rounded-lg">
                  <Globe size={16} weight="bold" className="text-blue-600" />
                </div>
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Our Vision
                </span>
              </div>
              <h3 className="font-bold text-2xl md:text-3xl mb-4 text-slate-900">
                The world's trend layer
              </h3>
              <p className="text-slate-600 text-base leading-relaxed">
                To become the definitive intelligence layer for global trends — a platform where
                journalists, founders, investors, students, and creators come first to understand
                what is happening and why it matters, before it matters everywhere else.
              </p>
            </div>
          </div>
        </section>

        <Divider />

        {/* ── What we cover ────────────────────────────────────────────────── */}
        <section aria-labelledby="coverage-heading">
          <SectionLabel>/ Coverage</SectionLabel>
          <h2
            id="coverage-heading"
            className="font-bold text-3xl md:text-4xl mt-3 mb-3 text-slate-900"
          >
            What we cover
          </h2>
          <p className="text-slate-500 text-base max-w-2xl mb-10">
            Six core verticals. Thousands of sources. One unified feed.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
            {COVERAGE_AREAS.map(({ icon: Icon, label, desc }) => (
              <div key={label} className="bg-white p-6 md:p-8 group hover:bg-slate-50 transition-colors">
                <Icon
                  size={24}
                  weight="duotone"
                  className="text-blue-600 mb-4"
                  aria-hidden="true"
                />
                <h3 className="font-bold text-lg mb-2 text-slate-900">{label}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        <Divider />

        {/* ── AI Features ──────────────────────────────────────────────────── */}
        <section aria-labelledby="ai-heading">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20 items-start">
            <div>
              <SectionLabel>/ AI-powered</SectionLabel>
              <h2
                id="ai-heading"
                className="font-bold text-3xl md:text-4xl mt-3 mb-6 text-slate-900"
              >
                Intelligence isn't optional anymore
              </h2>
              <p className="text-slate-600 text-base leading-relaxed mb-8">
                Every layer of TrendMing is augmented by AI — from the moment a story breaks to
                the second it lands in your feed. We don't just aggregate news; we contextualise,
                rank, and transform it into something actionable.
              </p>
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 text-sm rounded-lg transition-colors"
              >
                Unlock AI features <ArrowRight size={14} weight="bold" />
              </Link>
            </div>

            <ul className="space-y-0 border border-slate-200 divide-y divide-slate-200 rounded-xl overflow-hidden">
              {AI_FEATURES.map(({ label, desc }) => (
                <li key={label} className="flex items-start gap-4 px-6 py-5 bg-white hover:bg-slate-50 transition-colors">
                  <Sparkle
                    size={14}
                    weight="fill"
                    className="text-blue-600 mt-0.5 shrink-0"
                    aria-hidden="true"
                  />
                  <div>
                    <span className="font-bold text-sm text-slate-900">{label}</span>
                    <span className="text-slate-500 text-sm ml-2">— {desc}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <Divider />

        {/* ── Why choose us ────────────────────────────────────────────────── */}
        <section aria-labelledby="why-heading">
          <SectionLabel>/ Why TrendMing</SectionLabel>
          <h2
            id="why-heading"
            className="font-bold text-3xl md:text-4xl mt-3 mb-3 text-slate-900"
          >
            Why readers choose us
          </h2>
          <p className="text-slate-500 text-base max-w-2xl mb-10">
            The bar for news platforms is low. We set a different one.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {WHY_CHOOSE.map(({ icon: Icon, title, body }) => (
              <div
                key={title}
                className="border border-slate-200 bg-white p-6 md:p-8 rounded-xl"
              >
                <Icon
                  size={20}
                  weight="duotone"
                  className="text-blue-600 mb-4"
                  aria-hidden="true"
                />
                <h3 className="font-bold text-base mb-2 text-slate-900">{title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </section>

        <Divider />

        {/* ── Values strip ─────────────────────────────────────────────────── */}
        <section aria-label="Core values" className="grid grid-cols-1 md:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
          {[
            { heading: "Accuracy over speed", body: "We'd rather be second and right than first and wrong. Our credibility layer flags unverified claims automatically." },
            { heading: "Open perspectives", body: "We aggregate left, right, and centre. Our platform is a tool for understanding the world, not shaping your opinion of it." },
            { heading: "Privacy by default", body: "Your reading history stays yours. We personalise without selling data, and you can delete your profile at any time." },
          ].map(({ heading, body }) => (
            <div key={heading} className="bg-white px-8 py-10">
              <div className="w-1 h-6 bg-blue-600 mb-5" aria-hidden="true" />
              <h3 className="font-bold text-xl mb-3 text-slate-900">{heading}</h3>
              <p className="text-slate-500 text-sm leading-relaxed">{body}</p>
            </div>
          ))}
        </section>

        <Divider />

        {/* ── CTA ──────────────────────────────────────────────────────────── */}
        <section
          aria-labelledby="cta-heading"
          className="border border-blue-200 bg-blue-50 px-8 md:px-16 py-14 md:py-20 text-center rounded-2xl"
        >
          <SectionLabel>/ Get started</SectionLabel>
          <h2
            id="cta-heading"
            className="font-bold text-3xl md:text-5xl mt-4 mb-5 text-slate-900"
          >
            Start reading the world smarter
          </h2>
          <p className="text-slate-600 text-base md:text-lg max-w-xl mx-auto mb-10 leading-relaxed">
            Join thousands of professionals, creators, and curious minds who use TrendMing to stay
            ahead of every trend that matters.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/signup"
              className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 px-8 text-sm rounded-xl transition-colors"
            >
              Create free account <ArrowRight size={14} weight="bold" />
            </Link>
            <Link
              to="/pricing"
              className="inline-flex items-center justify-center gap-2 border border-slate-200 hover:bg-slate-50 text-slate-700 font-bold py-4 px-8 text-sm rounded-xl transition-colors"
            >
              View plans
            </Link>
          </div>
          <p className="text-slate-400 text-xs uppercase tracking-wider mt-8">
            Free forever · No credit card required
          </p>
        </section>

      </main>
    </>
  );
}
